import { offlineConnectivityStateAtom } from '@/atoms/offline-recording';
import { cachedIdentityBridge } from '@/tauri/offline-recording/identity';
import type { OfflineConnectivityUiState } from '@/tauri/offline-recording/offline-recording';
import { isTauri } from '@/tauri/utils';
import { useAtomValue } from 'jotai';
import { useEffect, useState } from 'react';
// TYPE-ONLY, and that is deliberate. This module is on the desktop boot path
// (`src/lib/auth/readFromProviderSourceOfTruth.ts` imports it), and the key list
// binds `@heidi/session/flags`. A type import is erased, so the allowlist costs
// the boot graph nothing and the dependency runs one way only: consumers import
// the keys and hand them to this mechanism.
import type { OfflineCachedFlagKey } from './offline-cached-flag-keys';

export const OFFLINE_RECORDING_FLAG_KEY = 'release-scribe-enable-offline-recording';

const STORAGE_PREFIX = 'heidi:offline-recording-flag:v1';
/**
 * Namespace for every allowlisted flag OTHER than the offline-recording one.
 *
 * Separate from {@link STORAGE_PREFIX} on purpose, and the reason is load-
 * bearing rather than cosmetic: {@link readAnyOfflineRecordingFlagDecision}
 * answers "is this MACHINE in the offline-recording rollout" by scanning every
 * key under `STORAGE_PREFIX:<env>:` and returning `true` on the first live
 * `true` it finds. Filing other flags' decisions under that prefix would make a
 * cached `session-page-v2: true` arm the offline auth fallback. The
 * offline-recording key also keeps its EXACT pre-existing storage key, so a
 * user who upgrades and immediately goes offline still finds their cache.
 */
const FLAG_STORAGE_PREFIX = 'heidi:offline-flag-decision:v1';
const MAX_AGE_MS = 1000 * 60 * 60 * 24 * 7;

type CachedFlagDecision = {
  value: boolean;
  storedAt: number;
};

const getEnvironmentKey = (): string =>
  process.env.NEXT_PUBLIC_FLAGSMITH_ENVIRONMENT_KEY ??
  process.env.NEXT_PUBLIC_ENVIRONMENT ??
  'unknown';

const getStorage = (): Storage | null => {
  if (typeof window === 'undefined') return null;
  try {
    return window.localStorage;
  } catch {
    return null;
  }
};

const getStorageKey = (userRefId: string): string =>
  `${STORAGE_PREFIX}:${getEnvironmentKey()}:${userRefId}`;

const getFlagStorageKey = (userRefId: string, flagKey: OfflineCachedFlagKey): string =>
  `${FLAG_STORAGE_PREFIX}:${getEnvironmentKey()}:${userRefId}:${flagKey}`;

/**
 * Shape guard, shared by every reader below. SHAPE ONLY — freshness is checked
 * separately by {@link isFresh}, because the two readers react to an expired
 * entry differently (one deletes it, one skips it) and folding the TTL in here
 * would quietly change which entries get evicted from a boot path.
 */
const parseDecision = (raw: string | null): CachedFlagDecision | null => {
  if (raw === null) return null;
  const parsed = JSON.parse(raw) as Partial<CachedFlagDecision>;
  if (typeof parsed.value !== 'boolean' || typeof parsed.storedAt !== 'number') {
    return null;
  }
  return { value: parsed.value, storedAt: parsed.storedAt };
};

const isFresh = (decision: CachedFlagDecision): boolean => {
  const age = Date.now() - decision.storedAt;
  return age >= 0 && age <= MAX_AGE_MS;
};

const pruneStorageNamespace = (storage: Storage, prefix: string): void => {
  for (let i = storage.length - 1; i >= 0; i -= 1) {
    const key = storage.key(i);
    if (key === null || !key.startsWith(prefix)) continue;
    try {
      const decision = parseDecision(storage.getItem(key));
      if (decision === null || !isFresh(decision)) storage.removeItem(key);
    } catch {
      try {
        storage.removeItem(key);
      } catch {
        // Cleanup is best-effort and must not prevent the current decision write.
      }
    }
  }
};

const isBrowserOffline = (): boolean =>
  typeof navigator !== 'undefined' && navigator.onLine === false;

/**
 * The fallback is eligible whenever the app does NOT hold a confirmed-online
 * reading — deliberately NOT "confirmed offline".
 *
 * Requiring a confirmed `offline` / `backend_unreachable` reading here was a
 * closed loop with no entry point on macOS:
 *
 *   cached flag fallback  needs a confirmed-offline reading
 *   confirmed reading     needs an armed Rust probe
 *   armed probe           needs `offline_connectivity_refresh_target(true)`
 *   `enabled: true`       needs this flag to already be on
 *
 * The browser signal is the only other way in, and macOS WKWebView pins
 * `navigator.onLine === true` (see `useNavigatorOnline`), so on a fully offline
 * macOS relaunch the atom sat at `unknown` forever, the flag stayed `false`, and
 * the probe was never armed. `unknown` therefore has to be eligible.
 *
 * Online remains authoritative in the way that matters: the instant a confirmed
 * `online_ready_to_sync` reading lands, the fallback stands down and remote
 * Flagsmith owns the answer again. The widened window only ever replays THIS
 * user's own last online decision, from a cache that expires after
 * {@link MAX_AGE_MS}.
 */
const isOfflineFlagFallbackState = (connectivity: OfflineConnectivityUiState): boolean =>
  isBrowserOffline() || connectivity !== 'online_ready_to_sync';

export function persistOfflineRecordingFlagDecision(
  userRefId: string | null | undefined,
  value: boolean
): void {
  if (!userRefId) return;

  const storage = getStorage();
  if (!storage) return;

  const decision: CachedFlagDecision = {
    value,
    storedAt: Date.now(),
  };

  try {
    pruneStorageNamespace(storage, `${STORAGE_PREFIX}:${getEnvironmentKey()}:`);
    storage.setItem(getStorageKey(userRefId), JSON.stringify(decision));
  } catch {
    // Best-effort cache only. Remote Flagsmith remains authoritative online.
  }
}

/**
 * Environment-scoped, user-agnostic read of the cached flag. Returns
 * `true` if ANY user on this machine has a cached decision of `true`
 * for the current environment (i.e. Flagsmith has told at least one
 * user on this device the offline-recording flag is on). Sync, safe
 * to call from module scope — used by callers that need to know
 * whether the offline-recording rollout has armed connectivity
 * signals on this machine, without first having a user identity.
 *
 * Trade-off: a stale-but-still-fresh cached `true` from a prior
 * signed-out user counts as "armed". Acceptable for consumers whose
 * behaviour is safe in both directions — treating an ex-flagged
 * machine as offline-capable does not resurrect auth; it only opens
 * the door for the offline auth fallback IF the session-shape
 * guards downstream also pass. Consumers that need a per-user answer
 * must still use `readCachedOfflineRecordingFlagDecision(userRefId)`.
 */
export function readAnyOfflineRecordingFlagDecision(): boolean {
  const storage = getStorage();
  if (!storage) return false;

  const envPrefix = `${STORAGE_PREFIX}:${getEnvironmentKey()}:`;
  try {
    for (let i = 0; i < storage.length; i += 1) {
      const key = storage.key(i);
      if (key === null || !key.startsWith(envPrefix)) continue;

      const decision = parseDecision(storage.getItem(key));
      if (decision === null || !isFresh(decision)) continue;
      if (decision.value) return true;
    }
    return false;
  } catch {
    return false;
  }
}

export function readCachedOfflineRecordingFlagDecision(
  userRefId: string | null | undefined
): boolean | null {
  if (!userRefId) return null;

  const storage = getStorage();
  if (!storage) return null;

  try {
    const decision = parseDecision(storage.getItem(getStorageKey(userRefId)));
    if (decision === null) return null;

    if (!isFresh(decision)) {
      storage.removeItem(getStorageKey(userRefId));
      return null;
    }

    return decision.value;
  } catch {
    return null;
  }
}

// ---------------------------------------------------------------------------
// The SAME mechanism, widened from one key to an allowlist of them.
//
// Last-known-value, the same seven-day bound, the same per-user and
// per-environment scoping — the only thing that changes is that the flag key is
// now an argument instead of a constant. See `offline-cached-flag-keys.ts` for
// which flags are covered and the bar for adding one.
// ---------------------------------------------------------------------------

/**
 * Record an allowlisted flag's ONLINE decision. Called only from
 * `OpenFeatureContextSync`, once the provider has settled on the current user's
 * context, so what lands here is always a value Flagsmith actually answered —
 * never a default standing in for one.
 */
export function persistCachedFlagDecision(
  userRefId: string | null | undefined,
  flagKey: OfflineCachedFlagKey,
  value: boolean
): void {
  if (!userRefId) return;

  const storage = getStorage();
  if (!storage) return;

  const decision: CachedFlagDecision = { value, storedAt: Date.now() };

  try {
    pruneStorageNamespace(storage, `${FLAG_STORAGE_PREFIX}:${getEnvironmentKey()}:`);
    storage.setItem(getFlagStorageKey(userRefId, flagKey), JSON.stringify(decision));
  } catch {
    // Best-effort cache only. Remote Flagsmith remains authoritative online.
  }
}

/** Read back one allowlisted flag's last online decision for a user. */
export function readCachedFlagDecision(
  userRefId: string | null | undefined,
  flagKey: OfflineCachedFlagKey
): boolean | null {
  if (!userRefId) return null;

  const storage = getStorage();
  if (!storage) return null;

  const storageKey = getFlagStorageKey(userRefId, flagKey);
  try {
    const decision = parseDecision(storage.getItem(storageKey));
    if (decision === null) return null;

    if (!isFresh(decision)) {
      storage.removeItem(storageKey);
      return null;
    }

    return decision.value;
  } catch {
    return null;
  }
}

/**
 * Whether the flag provider is CONFIRMED unreachable.
 *
 * Exported for `offline-cached-flag-decisions.ts`, the only caller.
 *
 * Deliberately stricter than {@link isOfflineFlagFallbackState}, which the
 * offline-recording gate uses. That one treats `unknown` as eligible to break a
 * chicken-and-egg loop that is specific to the flag arming the probe that
 * produces the reading. No other flag sits in that loop, and `unknown` is the
 * resting state of every desktop build whose probe is not armed — so accepting
 * it here would leave a cached `true` standing in for a reachable provider
 * saying `false`, i.e. a flag that can no longer be turned off. The whole point
 * of the cache is to replace a hardcoded default with a real past answer when
 * NOBODY can answer; it must never outrank somebody who can.
 */
export const isProviderUnreachable = (
  connectivity: OfflineConnectivityUiState
): boolean =>
  isBrowserOffline() ||
  connectivity === 'offline' ||
  connectivity === 'backend_unreachable';

/**
 * Cold offline desktop fallback for the offline-recording rollout gate.
 *
 * A user-scoped Flagsmith segment needs OpenFeature context from `user_ref_id`.
 * On a fully offline process restart, `/user/` and the identity endpoint may not
 * run soon enough to re-evaluate that segment. Use the keychain cached identity
 * to recover the last online decision, but only while the browser or Rust
 * backend-reachability monitor reports offline so online Flagsmith remains the
 * source of truth.
 */
export function useCachedOfflineRecordingFlagFallback(): boolean {
  return useCachedOfflineRecordingFlagArming().enabled;
}

/**
 * Whether this machine's cached-decision recovery has produced its answer yet.
 *
 * - `enabled` — the recovered decision (what {@link useCachedOfflineRecordingFlagFallback}
 *   returns).
 * - `settled` — `false` only while a recovery that CAN succeed is still in
 *   flight, i.e. this machine holds a fresh cached `true` for some user and the
 *   cached-identity read has not come back yet.
 *
 * `settled` exists because the boot redirect on `/` fires from an effect on the
 * FIRST commit. The identity read is an IPC round-trip, so a hook that starts at
 * `false` and corrects itself an instant later has already lost that race: the
 * redirect has been computed and dispatched against a connectivity reading of
 * `online`. Consumers use `!settled` to hold the boot loader for the length of
 * one local IPC call — never a network call, and never at all on a machine the
 * rollout has not touched, which is why the initial value is computed
 * synchronously here rather than in an effect.
 */
export function useCachedOfflineRecordingFlagArming(): {
  enabled: boolean;
  settled: boolean;
} {
  const connectivity = useAtomValue(offlineConnectivityStateAtom);
  // Synchronous, first-render answer. `readAnyOfflineRecordingFlagDecision` is a
  // localStorage-only read: a machine with no cached `true` has nothing to wait
  // for and settles immediately, so nothing about a non-rollout boot changes.
  const [state, setState] = useState<{ enabled: boolean; settled: boolean }>(() =>
    isTauri() && readAnyOfflineRecordingFlagDecision()
      ? { enabled: false, settled: false }
      : { enabled: false, settled: true }
  );

  useEffect(() => {
    // Identity-preserving, and that is load-bearing rather than tidiness.
    //
    // The effect below re-asserts the answer the initial state already computed
    // synchronously, so on most boots it writes a value equal to the one held.
    // Written as a plain object literal that is still a NEW object, React's
    // `Object.is` bail-out cannot fire and every consumer of this hook re-renders
    // once on mount for no change. That render is not free anywhere it lands, and
    // on the boot route it re-runs `/scribe`'s landing effect, whose non-overdue
    // gates re-derive their destination from inputs that have not changed — so a
    // consult already handed off is handed off a second time.
    //
    // Returning `prev` when both fields match keeps the answer identical and the
    // render count at what actually changed.
    const settleTo = (next: { enabled: boolean; settled: boolean }) =>
      setState((prev) =>
        prev.enabled === next.enabled && prev.settled === next.settled ? prev : next
      );

    if (!isTauri() || !isOfflineFlagFallbackState(connectivity)) {
      settleTo({ enabled: false, settled: true });
      return;
    }

    if (!readAnyOfflineRecordingFlagDecision()) {
      settleTo({ enabled: false, settled: true });
      return;
    }

    let cancelled = false;
    settleTo({ enabled: false, settled: false });
    void cachedIdentityBridge
      .read()
      .then((identity) => {
        if (cancelled) return;
        settleTo({
          enabled: readCachedOfflineRecordingFlagDecision(identity?.userId) === true,
          settled: true,
        });
      })
      .catch(() => {
        if (cancelled) return;
        // A failed read is an ANSWER, not a reason to hold the loader open —
        // bound every wait. But answering "off" throws away a decision we
        // already have: reaching this IPC at all required
        // `readAnyOfflineRecordingFlagDecision()` to be true, so this machine
        // holds a fresh cached `true`. The identity read only narrows that
        // machine-level answer to THIS user; when it fails there is nothing to
        // narrow with, and the machine-level answer is the honest one. Falling
        // to `false` instead would unarm the probe on a rollout machine purely
        // because a keychain read flaked.
        //
        // Re-read rather than assume: the cache is time-bounded, so the entry
        // that armed the initial state may have expired while the IPC was in
        // flight, and `readAny` is a localStorage-only call.
        settleTo({
          enabled: readAnyOfflineRecordingFlagDecision(),
          settled: true,
        });
      });

    return () => {
      cancelled = true;
    };
  }, [connectivity]);

  return state;
}
